import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { fetchUserProfile } from '../store/authSlice';
import { RootState, AppDispatch } from '../store';
import useSessionMonitor from '../hooks/useSessionMonitor';
import AppNavbar from './AppNavbar';

interface LayoutProps {
  children: React.ReactNode;
}

type UnitDecorativeEllipse = {
  top: string;
  left: string;
  width: string;
  height: string;
  color: string;
  blur?: string;
  mixBlendMode?: string;
  transform?: string;
};

const UNIT_DECORATIVE_ELLIPSES: UnitDecorativeEllipse[] = [
  { top: '102px', left: 'calc(20% + 230.6px)', width: '874px', height: '620px', color: '#F3EAE6' },
  { top: '844px', left: '-104px', width: '554px', height: '191px', color: '#F8FFDE' },
  { top: '1053px', left: 'calc(60% + 44.8px)', width: '749px', height: '318px', color: '#E7FCFF' },
  { top: '-80px', left: 'calc(20% + 229.6px)', width: '874px', height: '808px', color: '#F3EAE6' },
  { top: '73px', left: '-66px', width: '915px', height: '405px', color: '#F7F5F4', blur: '150px' },
  { top: '1310px', left: 'calc(40% + 204.2px)', width: '580px', height: '1139px', color: '#F5EFEC' },
  {
    top: '886px',
    left: 'calc(40% + 86.2px)',
    width: '928px',
    height: '391px',
    color: 'rgba(117, 109, 108, 0.5)',
    mixBlendMode: 'hard-light',
  },
  { top: '1789.38px', left: '-35.57px', width: '770.144px', height: '276.24px', color: '#E3FBFF' },
  { top: '2260px', left: '85px', width: '921px', height: '487px', color: '#FFF0E9' },
  {
    top: '2764px',
    left: 'calc(60% + 29.8px)',
    width: '317px',
    height: '199px',
    color: '#F8FFDE',
    transform: 'rotate(90deg)',
  },
  {
    top: '3184px',
    left: '-11px',
    width: '354px',
    height: '806px',
    color: 'rgba(117, 109, 108, 0.5)',
    mixBlendMode: 'hard-light',
  },
  { top: '3372px', left: 'calc(20% + 114.6px)', width: '1083px', height: '634px', color: '#FFF6F1' },
  { top: '3720px', left: 'calc(40% + 40.2px)', width: '883px', height: '318px', color: '#E7FCFF' },
  { top: '4186px', left: '140px', width: '921px', height: '487px', color: '#FFF0E9' },
  {
    top: '4216.16px',
    left: 'calc(60% + 160.8px)',
    width: '548.273px',
    height: '203.317px',
    color: '#F8FFDE',
    transform: 'rotate(111.73deg) skewX(1.68deg)',
  },
  { top: '4441px', left: '-64px', width: '352px', height: '302px', color: '#F9E1EC' },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { isAuthenticated, profile } = useSelector((state: RootState) => state.auth);
  const [appBarHeight, setAppBarHeight] = useState<number>(0);
  const isUnitRoute = location.pathname.startsWith('/unit/');
  const isBuildingRoute = location.pathname.startsWith('/building/');
  const isAgentShareRoute = location.pathname.startsWith('/agent/') ||
    ['/about-us/henry-zhao', '/about-us/calvin-luo'].includes(location.pathname.replace(/\/+$/, '').toLowerCase());
  const isPublicIntakeRoute = location.pathname.startsWith('/customer-intake/');
  const isLoginRoute = location.pathname === '/login' || location.pathname === '/business/login';

  useSessionMonitor();

  // Cached profile from secureStorage is minimal (name/avatar/email/phone only) and omits address
  // and other fields. `profile` is truthy after refresh, so we must not gate on `!profile` alone.
  useEffect(() => {
    if (isLoginRoute) return;
    if (isAuthenticated && (!profile || !('street' in profile))) {
      dispatch(fetchUserProfile());
    }
  }, [dispatch, isAuthenticated, isLoginRoute, profile]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        position: 'relative',
        // 'clip' (not 'hidden') so position:sticky children can pin to the window.
        overflowX: 'clip',
        background: isUnitRoute || isAgentShareRoute || isPublicIntakeRoute
          ? '#FFFAF7'
          : isBuildingRoute
          ? 'linear-gradient(180deg, #ffffff 0%, #e9e3e0 56.948%, #d3ccc9 99.807%)'
          : 'transparent',
        '--appbar-height': `${appBarHeight || 64}px`,
      } as any}
    >
      {isUnitRoute && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              maxWidth: 1512,
              mx: 'auto',
              width: '100%',
              height: '100%',
              position: 'relative',
            }}
          >
            {UNIT_DECORATIVE_ELLIPSES.map((ellipse, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  display: { xs: 'none', md: 'block' },
                  top: ellipse.top,
                  left: ellipse.left,
                  width: ellipse.width,
                  height: ellipse.height,
                  borderRadius: '50%',
                  backgroundColor: ellipse.color,
                  filter: `blur(${ellipse.blur ?? '200px'})`,
                  mixBlendMode: ellipse.mixBlendMode ?? 'normal',
                  transform: ellipse.transform,
                }}
              />
            ))}
          </Box>
        </Box>
      )}
      {isAgentShareRoute || isPublicIntakeRoute ? (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            position: 'relative',
            zIndex: 1,
            width: '100%',
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      ) : (
        <AppNavbar onAppBarHeightChange={setAppBarHeight}>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              position: 'relative',
              zIndex: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            {children}
          </Box>
        </AppNavbar>
      )}
    </Box>
  );
};

export default Layout;
